import React, {useEffect, useState} from "react";
import Header from "../component/menu/Header";
import {CharLimit as charLimit, constants} from "../constant/constants";
import SideMenu from "../component/menu/SideMenu";
import CustomLoader from "../component/menu/CustomLoader";
import {Button, Input, message, Modal, Spin} from "antd";
import ApiService from "../utils/ApiService";
import useUserStore from "../stores/store";
import {isOnlySpecialCharacters} from "../utils/Common";

function AddMealAddition() {
  const { user, restaurantId } = useUserStore();
  const [mealAddition, setMealAddition] = useState("");
  const [mealList, setMealList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [upsertLoading, setUpsertLoading] = useState(false);

  const userToken = user?.token?.token;

  // Fetch addition items list
  const fetchMealList = async () => {
    if (!restaurantId) {
      message.error("Restaurant ID is required");
      return;
    }

    try {
      setLoading(true);
      const response = await ApiService.GET_ADDITION_ITEM_LIST(restaurantId, userToken);

      if (response?.code !== 200) {
        message.error(response?.message || "Failed to fetch meal additions");
        return;
      }

      setMealList(response.payload.records || []);
    } catch (error) {
      console.error("Error fetching meal list:", error);
      message.error(error?.response?.data?.message || "An error occurred while fetching meal additions");
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchMealList();
  }, [restaurantId]);
  // ✅ Add Meal
  const addMeal = async () => {
    const trimmedMeal = mealAddition.trim();

    if (!trimmedMeal) {
      message.error("Please enter an item name.");
      return;
    }

    if (trimmedMeal.length > 100) {
      message.error("Item name cannot exceed 100 characters. Please shorten it.");
      return;
    }

    // Prevent names made of only special characters
    if (isOnlySpecialCharacters(trimmedMeal)) {
      message.error("Name cannot contain only special characters.");
      return;
    }

    const reqBody = {
      additionItemId: "", // Empty string indicates new item
      restaurantId,
      name: trimmedMeal,
    };
    try {
      setUpsertLoading(true)
      const response = await ApiService.UPSERT_ADDITION_ITEM(reqBody, userToken);

      if (response?.code !== 200) {
        message.error(response?.message || "Failed to add meal. Please try again.");
        return;
      }

      message.success("The item is added to the list successfully.");

      // fetch list of item
      fetchMealList();
    } catch (error) {
      console.error("Error adding meal:", error);
      message.error(error?.response?.data?.message || "Failed to add meal. Please try again.");
    }finally {
      setMealAddition(""); // Reset input
      setUpsertLoading(false)
    }
  }

  // ✅ Remove Meal with Modal Confirmation
  const confirmRemove = (additionItemId) => {
    Modal.confirm({
      title: "Are you sure?",
      content: `Do you really want to remove it?`,
      okText: "Yes",
      cancelText: "No",
      centered: true,
      onOk: () => {
        DeleteMealItem(additionItemId)
      },
    });
  };

  const DeleteMealItem = async (additionItemId) => {
    if (!additionItemId) {
      message.error("Invalid item ID");
      return;
    }

    try {
      const response = await ApiService.DELETE_ADDITION_ITEM(additionItemId, userToken);

      if (response?.code !== 200) {
        message.error(response?.message || "Failed to delete the addition item.");
        return;
      }

      // check it has success or not
      if (response.payload.success){
        message.success("The item is removed from the list successfully.");
        fetchMealList(); // Refresh the list after successful deletion
        return
      }

      const { dependentFoods = [], dependentOrders = [], dependentSetMenus = [] } = response?.payload || {};

      // If there are dependent orders, block deletion and show info message
      if (dependentOrders.length > 0) {
        message.warning(
          "This meal item is being used in the following orders. You can update it after the orders are completed."
        );
        return;
      }

      // If dependentFoods or dependentSetMenus exist, show confirmation modal
      if (dependentFoods.length > 0 || dependentSetMenus.length > 0) {
        Modal.confirm({
          title: "Dependencies Found",
          content: (
            <div>
              <p>
                This meal includes the following food items. If you delete the meal, these items will no longer be associated with it.
              </p>

              {dependentFoods.length > 0 && (
                <>
                  <p style={{ marginTop: 10, fontWeight: "bold" }}>Dependent Foods:</p>
                  <ul style={{ paddingLeft: 20 }}>
                    {dependentFoods.map((food) => (
                      <li key={food.id}>• {food.name}</li>
                    ))}
                  </ul>
                </>
              )}

              {dependentSetMenus.length > 0 && (
                <>
                  <p style={{ marginTop: 10, fontWeight: "bold" }}>Dependent Set Menus:</p>
                  <ul style={{ paddingLeft: 20 }}>
                    {dependentSetMenus.map((menu) => (
                      <li key={menu.id}>• {menu.name}</li>
                    ))}
                  </ul>
                </>
              )}

              <p style={{ marginTop: 15, fontWeight: "bold" }}>Do you want to continue?</p>
            </div>
          ),
          okText: "Yes, Proceed",
          cancelText: "Cancel",
          onOk: async () => {
            await ConfirmDeleteMealItem(additionItemId, dependentFoods, dependentSetMenus);
          },
        });
        return;
      }
    } catch (error) {
      console.error("Error deleting addition item:", error);
      message.error(error?.response?.data?.message || "Error occurred while deleting the item.");
    }
  };

  /**
   * Confirm deletion of the addition item (handles dependencies if any).
   * @param {string} additionItemId - The addition item ID.
   * @param {Array} dependentFoods - IDs of dependent foods.
   * @param {Array} dependentSetMenus - IDs of dependent set menus.
   */
  const ConfirmDeleteMealItem = async (additionItemId, dependentFoods, dependentSetMenus) => {
    try {
      const reqBody = {
        additionItemId,
        dependentFoods: dependentFoods.map(item => item.id),
        dependentSetMenus: dependentSetMenus.map(item => item.id),
      };

      const response = await ApiService.CONFIRM_DELETE_ADDITION_ITEM(reqBody, userToken);

      if (response?.code === 200) {
        message.success("Addition item deleted successfully.");
        fetchMealList(); // Refresh the list after successful deletion
      } else {
        message.error(response?.message || "Failed to confirm deletion.");
      }
    } catch (error) {
      console.error("Error confirming deletion:", error);
      message.error(error?.response?.data?.message || "Error occurred during confirm deletion.");
    }
  };

  return (
    <div className="flex flex-col bg-[#FAFAFA] min-h-screen">
      <CustomLoader />
      <Header
        title={`McDonalds Food Chain`}
        menu={constants.MENU.FOOD}
        currentMenuKey={"T_3_S_3"}
      />
      <div className="flex flex-row bg-prewhite">
        <SideMenu title={`McDonalds Food Chain`} menu={constants.MENU.FOOD} />
        <div className="flex flex-row p-[24px] w-full mt-[80px] ml-[72px]">
          <div className="flex flex-col w-full">
            {/* Page Title */}
            <div className="mb-[24px] text-2xl font-bold text-black">
              Add Meal Addition
            </div>

            {/* White Card */}
            <div className="bg-white rounded-[12px] shadow-sm px-20 py-6 w-[90%]">
              {/* Meal Addition Input */}
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <h3 className="text-gray-700 font-semibold whitespace-nowrap mr-4">
                    Items
                  </h3>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                <div className="flex gap-3 w-[80%]">
                  <div className="w-full">
                    <Input
                      placeholder="Item Name"
                      value={mealAddition}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Enforce max 100 characters
                        if (value.length > charLimit.Name) return;
                        setMealAddition(value);
                      }}
                      className="h-[40px] rounded-md border border-gray-300"
                      onPressEnter={addMeal}
                    />
                    <div className="mt-1 text-[12px] text-gray-600 italic">
                      Please ensure the item name does not exceed 100 characters. Current character count: {mealAddition.length}
                    </div>
                  </div>
                  <Button
                    className="bg-[#2EC114] hover:bg-[#28a10f] text-white font-ubold rounded-[5px] px-[24px] h-[40px]"
                    onClick={addMeal}>
                    {upsertLoading ? <Spin size={"small"} /> :"Add"}
                  </Button>
                </div>
              </div>

              {/* Meal Addition List */}
              <div>
                <div className="flex items-center mb-4">
                  <h3 className="text-gray-700 font-semibold whitespace-nowrap mr-4">
                    Item List
                  </h3>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                <div className="flex flex-col gap-4 w-[80%]">
                  {loading ? (
                    <div className="h-[30vh] flex items-center justify-center">
                      <Spin size="large" />
                    </div>
                  ):(
                    mealList.length > 0 ? (
                      mealList.map((item, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <img
                              src="/images/circle-list-icon.svg"
                              alt="list"
                              className="w-4 h-4"
                            />
                            <span className="font-semibold break-words break-all whitespace-normal pr-4">{item.name}</span>
                          </div>
                          <Button
                            type="text"
                            className="p-0 flex-shrink-0"
                            onClick={() => confirmRemove(item.additionItemId)}
                          >
                            <img
                              src="/images/remove-icon.svg"
                              alt="Remove"
                              className="w-6 h-6 cursor-pointer flex-shrink-0"
                            />
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-500 italic">
                        No meal additions yet. Add a new one above.
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddMealAddition;
